import { afterNextRender, Directive, ElementRef, inject, input, OnDestroy } from '@angular/core';

/**
 * Animated statistic. The template renders the FINAL value, so the server-side
 * HTML (and any reader with JS off) shows the real number; the browser only
 * rewinds it to zero when it is actually going to animate.
 */
@Directive({ selector: '[appCountUp]' })
export class CountUp implements OnDestroy {
  readonly appCountUp = input(0);
  readonly suffix = input('');
  readonly duration = input(1200);

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private observer?: IntersectionObserver;
  private frame = 0;

  constructor() {
    afterNextRender(() => {
      const node = this.el.nativeElement;
      if (typeof IntersectionObserver === 'undefined') { return; }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
      // Already scrolled past (deep link): leave the final value in place.
      if (node.getBoundingClientRect().bottom < 0) { return; }

      node.textContent = `0${this.suffix()}`;
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) { continue; }
          this.run();
          this.observer?.unobserve(node);
        }
      }, { threshold: 0.6 });
      this.observer.observe(node);
    });
  }

  private run(): void {
    const node = this.el.nativeElement;
    const target = this.appCountUp();
    let start: number | null = null;

    const step = (ts: number) => {
      start ??= ts;
      const p = Math.min((ts - start) / this.duration(), 1);
      node.textContent = `${Math.round(target * (1 - Math.pow(1 - p, 3)))}${this.suffix()}`;
      if (p < 1) { this.frame = requestAnimationFrame(step); }
    };
    this.frame = requestAnimationFrame(step);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame) { cancelAnimationFrame(this.frame); }
  }
}
